// src/components/legal/index.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const SiteHeader = () => (
  <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex-shrink-0">
          <Link to="/" className="text-xl font-bold text-sky-800">Mr Joel&apos;s Seller Tools</Link>
        </div>
        <nav className="hidden md:flex items-center space-x-2">
          <Link to="/#features" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">Features</Link>
          <Link to="/#how-to-use" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">How It Works</Link>
          <Link to="/#pricing" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">Pricing</Link>
          <Link to="/how-to-use" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">User Guide</Link>
          <Link to="/blog" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">Blog</Link>
          <Link to="/#faq" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">FAQ</Link>
          <a href="/app" className="text-slate-600 hover:text-sky-600 font-medium py-2 px-4 rounded-full text-sm">Login</a>
          <Link to="/#pricing" className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-full text-sm shadow-lg">Sign Up Free</Link>
        </nav>
      </div>
    </div>
  </header>
);

const SiteFooter = () => (
  <footer className="bg-slate-800 text-slate-400 py-12 mt-16">
    <div className="max-w-5xl mx-auto text-center px-4">
      <p>&copy; {new Date().getFullYear()} Mr Joel&apos;s Seller Tools. All Rights Reserved.</p>
      <div className="mt-4 space-x-4">
        <Link to="/contact" className="hover:text-white">Contact</Link>
        <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
        <Link to="/terms" className="hover:text-white">Terms of Service</Link>
      </div>
    </div>
  </footer>
);

const Index = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Mr Joel&apos;s Seller Tools</title>
        <meta name="description" content="Privacy Policy for Mr Joel’s Seller Tools: how we collect, use, share, and protect your data." />
        <meta property="og:title" content="Privacy Policy | Mr Joel's Seller Tools" />
        <meta property="og:description" content="Privacy Policy for Mr Joel’s Seller Tools: how we collect, use, share, and protect your data." />
        <meta property="og:url" content="https://mrjoelstptgenerator.com/privacy-policy" />
      </Helmet>

      <div className="bg-slate-50 text-slate-900 min-h-screen">
        <SiteHeader />

        <main className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-sky-800 tracking-tight">Privacy Policy</h1>
            <p className="mt-3 text-slate-600">Effective Date: <strong>June 15, 2025</strong></p>
            <p className="mt-6 text-slate-700">
              This Privacy Policy explains how Mr Joel’s Seller Tools (“we,” “us,” or “our”) collects, uses, shares, and
              safeguards information when you use our website, app, and related services (the “Services”).
            </p>
            <p className="mt-4 text-slate-700">
              We build tools for teacher-authors to import public TPT product data, improve listings, generate content, and
              analyze SEO performance. By using the Services, you agree to this Policy.
            </p>

            <section className="mt-10 space-y-6">
              <h2 className="text-2xl font-bold text-slate-800">1) Information We Collect</h2>

              <div>
                <h3 className="text-lg font-semibold text-slate-800">A. Information you provide</h3>
                <ul className="mt-2 list-disc list-inside text-slate-700 space-y-1">
                  <li><strong>Account details:</strong> email and (optionally) name when you sign up or log in.</li>
                  <li><strong>Content you submit:</strong> prompts, product titles/descriptions, keywords, and any other inputs used by our tools.</li>
                  <li><strong>Support communications:</strong> emails and in-app messages.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-800">B. Information collected automatically</h3>
                <ul className="mt-2 list-disc list-inside text-slate-700 space-y-1">
                  <li><strong>Usage &amp; device data:</strong> pages visited, features used, IP address, browser/OS, timestamps, crash logs.</li>
                  <li><strong>Cookies &amp; similar tech:</strong> to keep you logged in, remember preferences, attribute referrals, and measure analytics (see “Cookies”).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-800">C. From third parties / public sources</h3>
                <ul className="mt-2 list-disc list-inside text-slate-700 space-y-1">
                  <li><strong>Public TPT pages you choose to import:</strong> product titles, descriptions, images/links, and other publicly visible fields.</li>
                  <li><strong>Analytics/diagnostics:</strong> Google Analytics / Firebase Analytics event data.</li>
                  <li><strong>Affiliate/referral data:</strong> when you arrive via an affiliate link (see “Affiliate &amp; Referral Tracking”).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-800">D. Payments</h3>
                <p className="mt-2 text-slate-700">
                  Purchases are processed on Teachers Pay Teachers (TPT). We do not collect or store payment card details. We may
                  receive order confirmations to activate access.
                </p>
              </div>
            </section>

            <section className="mt-10 space-y-6">
              <h2 className="text-2xl font-bold text-slate-800">2) How We Use Information</h2>
              <p className="text-slate-700">We process data to:</p>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li><strong>Provide core features:</strong> account/login, importing public product pages you select, generating descriptions/keywords/social posts, rank tracking, saving your work.</li>
                <li><strong>Improve &amp; secure the Services:</strong> diagnostics, debugging, monitoring, preventing fraud/abuse, and enhancing features/performance.</li>
                <li><strong>Customer support:</strong> respond to questions and resolve issues.</li>
                <li><strong>Legal/compliance:</strong> enforce terms, protect rights, and comply with laws.</li>
              </ul>

              <div>
                <h3 className="text-lg font-semibold text-slate-800">AI features</h3>
                <p className="mt-2 text-slate-700">
                  When you use AI features, your prompts and relevant context are sent to third-party AI providers (e.g., Google
                  Generative AI) to generate results. We do not allow your content to be used to train our models. Third-party
                  providers process inputs under their own terms.
                </p>
              </div>
            </section>

            <section className="mt-10 space-y-6">
              <h2 className="text-2xl font-bold text-slate-800">3) Marketing &amp; Communications</h2>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li><strong>Service emails:</strong> account, security, and important product updates (sent regardless of marketing preferences).</li>
                <li><strong>Marketing emails (optional):</strong> tips, feature announcements, promotions, and success stories.</li>
              </ul>
              <p className="text-slate-700">
                You can unsubscribe anytime via the link in the email or by contacting <a href="mailto:info@mrjoelstptgenerator.com" className="text-sky-700 underline">info@mrjoelstptgenerator.com</a>.
              </p>
              <p className="text-slate-700"><strong>In-product messages:</strong> we may show banners/notices about new features, upgrades, or relevant offers.</p>
              <p className="text-slate-700"><strong>We do not sell your personal information.</strong></p>
            </section>

            <section className="mt-10 space-y-6">
              <h2 className="text-2xl font-bold text-slate-800">4) Cookies &amp; Similar Technologies</h2>
              <p className="text-slate-700">We use:</p>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li><strong>Essential cookies:</strong> authentication/session, security, preference storage.</li>
                <li><strong>Analytics cookies:</strong> usage metrics to improve the product (e.g., Google Analytics/Firebase Analytics).</li>
                <li><strong>Affiliate cookie (“rid”):</strong> set when you click an affiliate link to attribute signups/purchases (valid up to 90 days).</li>
              </ul>
              <p className="text-slate-700">
                You can control cookies via your browser settings. Blocking essential cookies may break some features.
              </p>
            </section>

            <section className="mt-10 space-y-6">
              <h2 className="text-2xl font-bold text-slate-800">5) Affiliate &amp; Referral Tracking</h2>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li>We set a short, random referral ID cookie (rid) for up to 90 days when you visit via an affiliate link.</li>
                <li>We may store a hashed IP and browser signature to prevent abuse.</li>
                <li>If you sign up during that period, we attribute your account to the affiliate for rewards.</li>
              </ul>
              <p className="text-slate-700">This supports creators who recommend our tool. We do not sell personal data.</p>
            </section>

            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">6) Legal Bases (EEA/UK)</h2>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li><strong>Performance of a contract</strong> to provide the Services you requested.</li>
                <li><strong>Legitimate interests</strong> for product improvement, fraud prevention, analytics, affiliate attribution (balanced against your rights).</li>
                <li><strong>Consent</strong> for marketing emails and non-essential cookies (where required).</li>
                <li><strong>Legal obligation</strong> to comply with applicable laws.</li>
              </ul>
            </section>

            <section className="mt-10 space-y-6">
              <h2 className="text-2xl font-bold text-slate-800">7) How We Share Information</h2>
              <p className="text-slate-700">We share information only with:</p>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li><strong>Service providers (“processors”):</strong> hosting, databases, analytics, logging, email, AI generation (e.g., Google Firebase/Analytics, Google Generative AI). They must protect your data and use it only to provide services to us.</li>
                <li><strong>Legal authorities:</strong> when required by law or to protect rights/safety.</li>
                <li><strong>Business transfers:</strong> in a merger, acquisition, or similar event, your data may transfer under this same Policy.</li>
              </ul>
              <p className="text-slate-700"><strong>We do not sell personal information.</strong></p>
            </section>

            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">8) Data Retention</h2>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li><strong>Account &amp; content:</strong> kept while your account is active (then deleted or anonymized upon request, subject to legal holds).</li>
                <li><strong>Referral records:</strong> retained as needed for program operation and fraud prevention (the cookie itself lasts up to 90 days).</li>
                <li><strong>Logs &amp; analytics:</strong> retained for a reasonable period to maintain and improve the Service.</li>
              </ul>
            </section>

            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">9) Security</h2>
              <p className="text-slate-700">
                We use industry-standard safeguards (HTTPS in transit, encryption at rest via our providers, role-based access,
                least-privilege). No method is 100% secure, but we work to protect your data.
              </p>
            </section>

            <section className="mt-10 space-y-6">
              <h2 className="text-2xl font-bold text-slate-800">10) Your Rights</h2>
              <p className="text-slate-700">Depending on your location, you may have the right to:</p>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li>Access, correct, or delete your personal information.</li>
                <li>Data portability (in certain cases).</li>
                <li>Object or restrict certain processing (e.g., analytics/marketing).</li>
                <li>Withdraw consent where processing is based on consent.</li>
                <li>Lodge a complaint with your supervisory authority.</li>
              </ul>
              <p className="text-slate-700">
                To exercise rights, email <a href="mailto:info@mrjoelstptgenerator.com" className="text-sky-700 underline">info@mrjoelstptgenerator.com</a>. We may request verification.
              </p>
              <p className="text-slate-700">
                <strong>California (CCPA/CPRA):</strong> You can request access, deletion, and opt out of “selling” or “sharing” (we
                do not sell personal info). You may limit use of sensitive info (we do not collect sensitive categories for typical use).
              </p>
            </section>

            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">11) Children’s Privacy</h2>
              <p className="text-slate-700">
                The Services are not directed to children under 13 (or the age required in your country). We do not knowingly
                collect personal information from children.
              </p>
            </section>

            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">12) International Transfers</h2>
              <p className="text-slate-700">
                We (and our providers) may process data in the United States and other countries. Where required, we use
                appropriate safeguards for cross-border transfers.
              </p>
            </section>

            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">13) Third-Party Links</h2>
              <p className="text-slate-700">
                Links to third-party sites (e.g., TPT) are governed by those sites’ privacy policies. We are not responsible for
                their practices.
              </p>
            </section>

            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">14) Changes to This Policy</h2>
              <p className="text-slate-700">
                We may update this Policy from time to time. We’ll update the “Effective Date” and, for significant changes,
                notify you in-app or by email.
              </p>
            </section>

            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">15) Contact Us</h2>
              <p className="text-slate-700">
                Questions, requests, or privacy concerns? <br />
                <span role="img" aria-label="email">📧</span>{' '}
                <a href="mailto:info@mrjoelstptgenerator.com" className="text-sky-700 underline">
                  info@mrjoelstptgenerator.com
                </a>
              </p>
            </section>
          </div>
        </main>

        <SiteFooter />
      </div>
    </>
  );
};

export default Index;
